import React, { useEffect, useState } from 'react';
import { useSignUp } from '@clerk/clerk-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Link,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Alert,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { Visibility, VisibilityOff, CheckCircleOutline } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import RoxburghBackground from '../assets/images/roxburgh-background.jpg';

const AcceptInvitation: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isLoaded, signUp } = useSignUp();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isVerifying, setIsVerifying] = useState(true);
  const [invitationValid, setInvitationValid] = useState(false);
  const [userEmail, setUserEmail] = useState<string>('');
  const [accountCreated, setAccountCreated] = useState(false);

  const currentYear = new Date().getFullYear();

  // Get the invitation ticket from URL params
  const ticket = searchParams.get('__clerk_ticket');

  useEffect(() => {
    if (!isLoaded || !signUp) return;

    const verifyInvitation = async () => {
      if (!ticket) {
        setError(t('acceptInvitation.errors.noTicket'));
        setIsVerifying(false);
        return;
      }

      try {
        // Create a sign-up attempt with the invitation ticket
        const result = await signUp.create({
          strategy: 'ticket',
          ticket,
        });

        if (result.status === 'missing_requirements') {
          // Invitation is valid, user needs to set password
          setUserEmail(result.emailAddress || '');
          setInvitationValid(true);
        } else if (result.status === 'complete') {
          // User already completed sign-up, redirect to login
          setAccountCreated(true);
        }
      } catch (err: unknown) {
        console.error('Invitation verification error:', err);
        const clerkError = getClerkError(err);
        if (clerkError.code === 'form_identifier_exists') {
          setError(t('acceptInvitation.errors.alreadyAccepted'));
        } else if (clerkError.code === 'form_param_nil') {
          setError(t('acceptInvitation.errors.invalidTicket'));
        } else {
          setError(clerkError.longMessage || t('acceptInvitation.errors.verificationFailed'));
        }
      } finally {
        setIsVerifying(false);
      }
    };

    verifyInvitation();
  }, [isLoaded, ticket, signUp, t]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!signUp) {
      setError(t('acceptInvitation.errors.setupFailed'));
      return;
    }

    // Validate passwords
    if (password.length < 8) {
      setError(t('acceptInvitation.validation.passwordMinLength'));
      return;
    }

    if (password !== confirmPassword) {
      setError(t('acceptInvitation.validation.passwordMismatch'));
      return;
    }

    setIsSubmitting(true);

    try {
      const result = await signUp.update({
        password,
      });
      if (result.status === 'complete') {
        setAccountCreated(true);
      } else {
        setError(t('acceptInvitation.errors.unexpectedStatus'));
      }
    } catch (err: unknown) {
      console.error('Password setup error:', err);
      const clerkError = getClerkError(err);
      if (clerkError.code === 'form_password_pwned') {
        setError(t('acceptInvitation.validation.passwordCompromised'));
      } else if (clerkError.code === 'form_password_length_too_short') {
        setError(t('acceptInvitation.validation.passwordMinLength'));
      } else {
        setError(clerkError.longMessage || t('acceptInvitation.errors.setupFailed'));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleTogglePassword = () => setShowPassword(!showPassword);
  const handleToggleConfirmPassword = () => setShowConfirmPassword(!showConfirmPassword);

  // Show loading state
  if (!isLoaded || isVerifying) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          gap: 2,
        }}
      >
        <CircularProgress />
        <Typography variant="textMD" color="text.secondary">
          {t('acceptInvitation.verifying')}
        </Typography>
      </Box>
    );
  }

  // Show success message after account creation
  if (accountCreated) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          padding: 3,
          maxWidth: 500,
          margin: '0 auto',
          textAlign: 'center',
        }}
      >
        <CheckCircleOutline 
          sx={{ 
            fontSize: 64, 
            color: 'success.main', 
            mb: 2 
          }} 
        />
        <Typography variant="displayXS" component="h1" sx={{ mb: 2 }}>
          {t('acceptInvitation.success.title')}
        </Typography>
        <Typography variant="textMD" color="text.secondary" sx={{ mb: 4 }}>
          {t('acceptInvitation.success.message')}
        </Typography>
        <Button variant="primary" size="lg" onClick={() => navigate('/login')}>
          {t('acceptInvitation.success.loginButton')}
        </Button>
      </Box>
    );
  }

  // Show error if invitation is invalid
  if (!invitationValid) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          padding: 3,
          maxWidth: 500,
          margin: '0 auto',
        }}
      >
        <Alert severity="error" sx={{ mb: 3, width: '100%' }}>
          {error || t('acceptInvitation.errors.invalidInvitation')}
        </Alert>
        <Button variant="primary" onClick={() => navigate('/login')}>
          {t('acceptInvitation.goToLogin')}
        </Button>
      </Box>
    );
  }

  return (
    <Grid container sx={{ height: '100vh', overflow: 'hidden', bgcolor: 'background.paper' }}>
      {/* Left Side - Password Setup Form */}
      <Grid
        item
        xs={12}
        md={5}
        lg={5}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          p: { xs: 4, md: 6, lg: 8 },
          height: '100%',
        }}
      >
        {/* Form Container */}
        <Box
          sx={{
            width: '100%',
            maxWidth: 380,
            mx: 'auto',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            flex: 1,
          }}
        >
          <Box sx={{ mb: 4 }}>
            <Typography variant="displayXS" component="h1" sx={{ mb: 1, color: 'text.primary' }}>
              {t('acceptInvitation.title')}
            </Typography>
            <Typography variant="textMD" sx={{ color: 'text.secondary' }}>
              {t('acceptInvitation.subtitle')}
            </Typography>
          </Box>

          {userEmail && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="textSM" sx={{ color: 'text.secondary' }}>
                {t('acceptInvitation.emailLabel')}
              </Typography>
              <Typography variant="textMD" sx={{ fontWeight: 500 }}>
                {userEmail}
              </Typography>
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label={t('acceptInvitation.fields.password')}
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              sx={{ mb: 2 }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={handleTogglePassword} edge="end">
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              label={t('acceptInvitation.fields.confirmPassword')}
              type={showConfirmPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              sx={{ mb: 3 }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={handleToggleConfirmPassword} edge="end">
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Typography variant="textSM" sx={{ color: 'text.secondary', mb: 3, display: 'block' }}>
              {t('acceptInvitation.passwordRequirements')}
            </Typography>

            <Button
              type="submit"
              variant="primary"
              fullWidth
              size="lg"
              disabled={isSubmitting || !password || !confirmPassword}
              sx={{ height: 48 }}
            >
              {isSubmitting ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                t('acceptInvitation.createAccount')
              )}
            </Button>
          </Box>

          <Box sx={{ mt: 3, textAlign: 'center' }}>
            <Typography variant="textSM" sx={{ color: 'text.secondary' }}>
              {t('acceptInvitation.alreadyHaveAccount')}{' '}
              <Link href="/login" underline="hover">
                {t('authentication.signIn')}
              </Link>
            </Typography>
          </Box>
        </Box>

        {/* Footer */}
        <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
          <Typography variant="textSM" sx={{ color: 'text.secondary' }}>
            {t('authentication.copyright', { year: currentYear })}
          </Typography>
          <Link href="#" underline="hover" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
            {t('authentication.privacyPolicy')}
          </Link>
          <Link
            href="mailto:help@roxburgh.co.uk"
            underline="hover"
            sx={{ color: 'text.secondary', fontSize: '0.875rem' }}
          >
            {t('authentication.help')}
          </Link>
        </Box>
      </Grid>

      {/* Right Side - Visual */}
      {!isMobile && (
        <Grid
          item
          md={7}
          lg={7}
          sx={{
            height: '100%',
            display: 'flex',
          }}
        >
          <Box
            sx={{
              flex: 1,
              borderTopLeftRadius: 80,
              borderBottomLeftRadius: 80,
              overflow: 'hidden',
              position: 'relative',
              backgroundImage: `url(${RoxburghBackground})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              boxShadow: '-20px 0 40px rgba(0,0,0,0.1)',
            }}
          />
        </Grid>
      )}
    </Grid>
  );
};

export default AcceptInvitation;

function getClerkError(error: unknown): { code?: string; longMessage?: string } {
  if (typeof error !== 'object' || error === null || !('errors' in error)) {
    return {};
  }

  const errors = (error as { errors?: unknown }).errors;
  if (!Array.isArray(errors) || typeof errors[0] !== 'object' || errors[0] === null) {
    return {};
  }

  const first = errors[0] as { code?: unknown; longMessage?: unknown };
  return {
    code: typeof first.code === 'string' ? first.code : undefined,
    longMessage: typeof first.longMessage === 'string' ? first.longMessage : undefined,
  };
}
